import { useCallback, useEffect, useRef, useState } from 'react';
import { supabase, SUPABASE_PUBLISHABLE_KEY } from '@/shared/integrations/supabase/client';
import {
  isMockLookupAssetSpecsEnabled,
  MOCK_LOOKUP_ASSET_SPECS_RESPONSE,
} from '@/features/assets/services/mockLookupAssetSpecs';
import { toast } from '@/shared/hooks/use-toast';
import { captureEdgeFailure } from '@/shared/integrations/sentry/reportEdgeFailure';
import type {
  LookupAssetSpecsResponse,
  LookupAssetSpecsStatus,
  SpecSheetRef,
} from '@/features/assets/types/lookupAssetSpecs';

const COMPLETE_STATUS = 'COMPLETE';
const MOCK_LOAD_MS = 400;

export interface UseLookupAssetSpecsParams {
  assetId: string | undefined;
  processingStatus: string | null | undefined;
  enabled: boolean;
}

export interface UseLookupAssetSpecsResult {
  status: LookupAssetSpecsStatus;
  skipReason: string | null;
  primaryPdf: SpecSheetRef | null;
  relatedPdfs: SpecSheetRef[];
  source: LookupAssetSpecsResponse['source'] | null;
  message: string | null;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

function applyLookupResponse(
  body: LookupAssetSpecsResponse,
  setters: {
    setStatus: (s: LookupAssetSpecsStatus) => void;
    setSource: (s: LookupAssetSpecsResponse['source'] | null) => void;
    setMessage: (m: string | null) => void;
    setPrimaryPdf: (p: SpecSheetRef | null) => void;
    setRelatedPdfs: (r: SpecSheetRef[]) => void;
  },
) {
  setters.setSource(body.source);
  setters.setMessage(body.message ?? null);
  setters.setPrimaryPdf(body.primary_pdf ?? null);
  setters.setRelatedPdfs(body.related_pdfs ?? []);

  const hasPdfs =
    Boolean(body.primary_pdf?.file_url) ||
    (body.related_pdfs?.some((p) => p.file_url) ?? false);

  if (body.success && hasPdfs) {
    setters.setStatus('success');
  } else if (body.source === 'related_only' && hasPdfs) {
    setters.setStatus('success');
  } else if (!body.success || body.source === 'not_found') {
    setters.setStatus('not_found');
  } else {
    setters.setStatus('success');
  }
}

export function useLookupAssetSpecs({
  assetId,
  processingStatus,
  enabled,
}: UseLookupAssetSpecsParams): UseLookupAssetSpecsResult {
  const requestSeqRef = useRef(0);
  const [status, setStatus] = useState<LookupAssetSpecsStatus>('idle');
  const [skipReason, setSkipReason] = useState<string | null>(null);
  const [primaryPdf, setPrimaryPdf] = useState<SpecSheetRef | null>(null);
  const [relatedPdfs, setRelatedPdfs] = useState<SpecSheetRef[]>([]);
  const [source, setSource] = useState<LookupAssetSpecsResponse['source'] | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const applyResponse = useCallback((body: LookupAssetSpecsResponse) => {
    applyLookupResponse(body, {
      setStatus,
      setSource,
      setMessage,
      setPrimaryPdf,
      setRelatedPdfs,
    });
  }, []);

  const fetchSpecs = useCallback(async () => {
    const requestSeq = ++requestSeqRef.current;
    const isStale = () => requestSeq !== requestSeqRef.current;

    if (!assetId || !enabled) {
      if (isStale()) return;
      setStatus('idle');
      setPrimaryPdf(null);
      setRelatedPdfs([]);
      setSource(null);
      setMessage(null);
      setSkipReason(null);
      setError(null);
      return;
    }

    const useMock = isMockLookupAssetSpecsEnabled();

    if (!useMock && processingStatus !== COMPLETE_STATUS) {
      if (isStale()) return;
      setStatus('skipped');
      setSkipReason('Spec sheets are available after AI processing completes.');
      setPrimaryPdf(null);
      setRelatedPdfs([]);
      setSource(null);
      setMessage(null);
      setError(null);
      setLoading(false);
      return;
    }

    if (isStale()) return;
    setLoading(true);
    setError(null);
    setSkipReason(null);

    try {
      if (useMock) {
        await new Promise((r) => setTimeout(r, MOCK_LOAD_MS));
        if (isStale()) return;
        applyResponse(MOCK_LOOKUP_ASSET_SPECS_RESPONSE);
        return;
      }

      // lookup-asset-specs is deployed with verify_jwt=true (facilities-flutter-app default).
      // The Supabase client accessToken is a Firebase JWT, which the gateway rejects as
      // UNAUTHORIZED_ASYMMETRIC_JWT. Send the anon key (valid Supabase JWT) instead;
      // the function uses service role internally (same as process-asset-ai chain).
      const { data, error: invokeError } = await supabase.functions.invoke<LookupAssetSpecsResponse>(
        'lookup-asset-specs',
        {
          body: { asset_id: assetId },
          headers: { Authorization: `Bearer ${SUPABASE_PUBLISHABLE_KEY}` },
        },
      );

      if (invokeError) {
        throw new Error(invokeError.message || 'Failed to load spec sheets');
      }

      const body = data as LookupAssetSpecsResponse | null;
      if (!body) {
        throw new Error('Empty response from spec sheet lookup');
      }

      if (isStale()) return;
      applyResponse(body);
    } catch (err) {
      if (isStale()) return;
      captureEdgeFailure({ endpoint: 'lookup-asset-specs', error: err });
      const msg = err instanceof Error ? err.message : 'Failed to load spec sheets';
      setError(msg);
      setStatus('error');
      setPrimaryPdf(null);
      setRelatedPdfs([]);
      toast({
        title: 'Spec sheet lookup failed',
        description: msg,
        variant: 'destructive',
      });
    } finally {
      if (!isStale()) {
        setLoading(false);
      }
    }
  }, [applyResponse, assetId, enabled, processingStatus]);

  useEffect(() => {
    void fetchSpecs();
  }, [fetchSpecs]);

  return {
    status,
    skipReason,
    primaryPdf,
    relatedPdfs,
    source,
    message,
    loading,
    error,
    refetch: fetchSpecs,
  };
}
